'use client'

import { featureFlagKeys } from 'feature-flags/constants/featureFlags'
import { useFeatureFlag } from 'feature-flags/hooks/useFeatureFlag/useFeatureFlag'
import { HELP_URLS } from 'help/constants/URLs'
import { ClientLabel } from 'labels/client'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMemo } from 'react'
import { RETURN_TYPES } from 'shipping-and-returns/constants/returnTypes'
import { Alert } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/Alert/Alert'
import { ContentContainer } from 'shipping-and-returns/containers/ReturnMethodsInfoContainer/components/ContentContainer/ContentContainer'
import { sendOnClickHomePickUp } from 'shipping-and-returns/utils/analytics/analyticsEvents'
import { getReturnsUrlByReturnType } from 'shipping-and-returns/utils/getReturnsUrlByReturnType/getReturnsUrlByReturnType'

import texts from 'fukku/styles/texts.module.scss'

interface HomeDeliveryContentProps {
	readonly hasReturnCostSinceDate: boolean
	readonly isBlocked: boolean
	readonly isFreePickUp: boolean
	readonly isRequestNeeded: boolean
	readonly returnCost: string
	readonly hasSecondReturnsBlock: boolean
	readonly hasDropPointRequest: boolean
	readonly orderId?: string
	readonly purchaseId?: string
}
export function HomeDeliveryContent({
	hasReturnCostSinceDate,
	isBlocked,
	isFreePickUp,
	isRequestNeeded,
	returnCost,
	hasSecondReturnsBlock,
	hasDropPointRequest,
	orderId,
	purchaseId,
}: HomeDeliveryContentProps) {
	const { t } = useLabels()
	const { enabled: isNewHelpEnabled } = useFeatureFlag(featureFlagKeys.HelpPage)

	const actionHref = useMemo(
		() =>
			isRequestNeeded
				? getReturnsUrlByReturnType({
						returnType: RETURN_TYPES.HOME_PICK_UP,
						orderId,
						purchaseId,
					})
				: HELP_URLS.HOME_PICKUP_RETURN,
		[orderId, purchaseId]
	)

	const handleHomePickUp = () => {
		sendOnClickHomePickUp()
	}

	const returnCostPlaceholderValues = {
		1: returnCost,
	}

	const labels = isRequestNeeded
		? {
				description:
					'myPurchases.online.details.returns.homePickup.withRequest.bodycopy',
				action: 'myPurchases.online.details.returns.requestPickup.button',
			}
		: {
				description:
					'myPurchases.online.details.returns.homePickup.noRequest.bodycopy',
				action: 'myPurchases.online.details.returns.seeInstructions.button',
			}

	return isBlocked ? (
		<ContentContainer
			description={t('myReturns.returnOptions.blocked.bodycopy')}
		/>
	) : (
		<ContentContainer
			description={
				<>
					<p>
						<ClientLabel>{t(labels.description)}</ClientLabel>
					</p>
					{hasSecondReturnsBlock && (
						<p
							data-testid='myReturns.returnOptions.homePickup.blockingInfo'
							className={texts.bodyMRegular}
						>
							<ClientLabel>
								{t(
									hasDropPointRequest
										? 'myReturns.returnOptions.infoBlocking.several.bodycopy'
										: 'myReturns.returnOptions.infoBlocking.bodycopy'
								)}
							</ClientLabel>
						</p>
					)}
				</>
			}
			{...(!isFreePickUp && {
				alert: hasReturnCostSinceDate ? (
					<Alert severity='importantAlert' hideIcon={false}>
						<ClientLabel placeholderValues={returnCostPlaceholderValues}>
							{t('myPurchases.online.returns.homePickup.newCost.text')}
						</ClientLabel>
					</Alert>
				) : (
					<Alert hideIcon>
						<ClientLabel placeholderValues={returnCostPlaceholderValues}>
							{t('myPurchases.online.returns.homePickup.cost.text')}
						</ClientLabel>
					</Alert>
				),
			})}
			action={{
				onClick: handleHomePickUp,
				dataTestId: labels.action,
				text: t(labels.action),
				linkAttributes: {
					href: actionHref,
					withLegacyLocale: !isNewHelpEnabled && !isRequestNeeded,
				},
			}}
		/>
	)
}
